import {
  AdproductsMoodysReportType,
  type CustomerAnalyticsMoodysMarketInventoryDto,
  type CustomerAnalyticsMoodysReportDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { MarketTrendsColumn, MarketTrendsPageWrapper, MarketTrendsTextHeader } from './MarketTrendsComponents';
import { Image, Text, View } from '@react-pdf/renderer';
import { styles } from './pdfStyles';
import { getRowStyle } from './pdfImageUtils';

export const InventoryPage = ({
  report,
  yearlyInventoryChart,
  showQuarterly,
}: {
  report: CustomerAnalyticsMoodysReportDto;
  yearlyInventoryChart: string;
  showQuarterly: boolean;
}) => {
  const totalCols = showQuarterly ? 8 : 4;
  const colWidth = 100 / totalCols;
  const threeYearsAgo = new Date().getFullYear() - 3;

  const convertToLocaleString = (value: number | undefined) => {
    return value ? value.toLocaleString() : '-';
  };

  const yearSummaries: Array<CustomerAnalyticsMoodysMarketInventoryDto> = report.inventoryReport
    .filter((row) => (showQuarterly ? row.year >= threeYearsAgo : row.quarter === 'Y'))
    .sort((a, b) => a.quarter.localeCompare(b.quarter))
    .sort((a, b) => a.year - b.year);

  return (
    <MarketTrendsPageWrapper quarter={report.dataAsOfQuarter} year={report.dataAsOfYear}>
      <View style={styles.containerBase}>
        <MarketTrendsTextHeader
          headerText={
            (report.reportType === AdproductsMoodysReportType.SUBMARKET ? 'Submarket' : 'Market') +
            ' Inventory' +
            (showQuarterly ? ' by Quarter' : ' by Year')
          }
          paddingBottom={20}
        />
        <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'center', marginBottom: 40 }}>
          <View>
            <Image src={yearlyInventoryChart} />
          </View>
        </View>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <MarketTrendsColumn width={colWidth}>
              <Text>Year</Text>
            </MarketTrendsColumn>
            {showQuarterly && (
              <MarketTrendsColumn width={colWidth}>
                <Text>Quarter</Text>
              </MarketTrendsColumn>
            )}
            <MarketTrendsColumn width={colWidth}>
              <Text>Inventory</Text>
              <Text style={styles.tableSubHeading}>Per Unit</Text>
            </MarketTrendsColumn>
            {showQuarterly && (
              <>
                <MarketTrendsColumn width={colWidth}>
                  <Text>Completions</Text>
                  <Text style={styles.tableSubHeading}>Per Unit</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={colWidth}>
                  <Text>Inventory</Text>
                  <Text style={styles.tableSubHeading}>% Growth</Text>
                </MarketTrendsColumn>
              </>
            )}
            <MarketTrendsColumn width={colWidth}>
              <Text>Vacant Stock</Text>
              <Text style={styles.tableSubHeading}>In Units</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={colWidth}>
              <Text>Occupied Stock</Text>
              <Text style={styles.tableSubHeading}>In Units</Text>
            </MarketTrendsColumn>
            {showQuarterly && (
              <MarketTrendsColumn width={colWidth}>
                <Text>Net Absorption</Text>
                <Text style={styles.tableSubHeading}>In Units</Text>
              </MarketTrendsColumn>
            )}
          </View>
          {yearSummaries.map((reportRow, i) => (
            <View style={getRowStyle(i, showQuarterly && reportRow.quarter === 'Y')} key={reportRow.year}>
              <MarketTrendsColumn width={colWidth}>
                <Text>{reportRow.year}</Text>
              </MarketTrendsColumn>
              {showQuarterly && (
                <MarketTrendsColumn width={colWidth}>
                  <Text>{reportRow.quarter}</Text>
                </MarketTrendsColumn>
              )}
              <MarketTrendsColumn width={colWidth}>
                <Text>{convertToLocaleString(reportRow.inventoryPerUnit)}</Text>
              </MarketTrendsColumn>
              {showQuarterly && (
                <>
                  <MarketTrendsColumn width={colWidth}>
                    <Text>{convertToLocaleString(reportRow.completionsPerUnit)}</Text>
                  </MarketTrendsColumn>
                  <MarketTrendsColumn width={colWidth}>
                    <Text>{(reportRow.inventoryPercentGrowth || 0 * 100).toFixed(1) + '%'}</Text>
                  </MarketTrendsColumn>
                </>
              )}
              <MarketTrendsColumn width={colWidth}>
                <Text>{convertToLocaleString(reportRow.vacantStockInUnits)}</Text>
              </MarketTrendsColumn>
              <MarketTrendsColumn width={colWidth}>
                <Text>{convertToLocaleString(reportRow.occupiedStockInUnits)}</Text>
              </MarketTrendsColumn>
              {showQuarterly && (
                <MarketTrendsColumn width={colWidth}>
                  <Text>{convertToLocaleString(reportRow.netAbsorptionInUnits)}</Text>
                </MarketTrendsColumn>
              )}
            </View>
          ))}
        </View>
      </View>
    </MarketTrendsPageWrapper>
  );
};
